import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  useState,
  useRef,
  useEffect,
  ReactNode,
  useContext,
} from 'react';
import { format, addMonths, parse } from 'date-fns';
import DatePicker, { CalendarContainer, registerLocale } from 'react-datepicker';
import { useClickOutside } from '../../utils/useClickOutside';
import { ReactComponent as CloseIcon } from '../../pages/SearchPage/assets/close.svg';
import { DatePresets } from './DatePresets';
import { DateRangeInput } from './DateRangeInput';
import './ReactDatepicker.css';
import styles from './Datepicker.module.scss';
import { DatepickerContext } from './context/context';
import { enGB as en, uk } from "date-fns/locale";

interface DatepickerProps {
  onChange: (start: Date | null, end: Date | null) => void;
  initStartDate: Date | null;
  initEndDate: Date | null;
  isRange: boolean;
  buttonDateFormat?: string;
  formError?: string;
  disabled?: boolean;
  className?: string;
  emptyLabel?: string;
  // renders the emptyLabel as a normal value (dark text) instead of the gray
  // placeholder look — for labels like "All time" that ARE the selected state
  emptyLabelActive?: boolean;
  clearable?: boolean;
  // opt-outs for hosts that pick a single day rather than a reporting range
  showPresets?: boolean;
  monthsShown?: number;
  maxDate?: Date;
  // adds the "All time" preset (clears both bounds) and lets Apply confirm the
  // empty selection — only for pickers whose empty state means "whole history"
  allTimePreset?: boolean;
}

export const Datepicker = ({
  onChange = () => {},
  initStartDate,
  initEndDate,
  isRange = true,
  buttonDateFormat = 'dd/MM/yyyy',
  formError = '',
  disabled = false,
  className,
  emptyLabel,
  emptyLabelActive = false,
  clearable = false,
  showPresets = true,
  monthsShown = 2,
  maxDate,
  allTimePreset = false,
}: DatepickerProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [calendarDate, setCalendarDate] = useState<Date>(initStartDate || new Date());
  const [calendarResetKey, setCalendarResetKey] = useState(0);
  const clickRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { state, dispatch } = useContext(DatepickerContext);
  registerLocale('en-GB', en);
  registerLocale('uk', uk);

  const lang = localStorage.getItem('i18nextLng');
  const datepickerLocale =
    lang === 'ua-UK' || lang === 'ru-RU' || lang === 'ru' || lang === 'uk'
      ? 'uk'
      : 'en-GB';


  useClickOutside(clickRef, () => {
    if (isOpen) {
      dispatch({ type: 'SET_START_DATE', payload: initStartDate });
      dispatch({ type: 'SET_END_DATE', payload: initEndDate });
      dispatch({ type: 'SET_START_DATE_ERROR', payload: '' });
      dispatch({ type: 'SET_END_DATE_ERROR', payload: '' });
      dispatch({ type: 'SET_RANGE_ERROR', payload: '' });
      setIsOpen(false);
    } 
  });

  useEffect(() => {
    if (initStartDate) {
      dispatch({ type: 'SET_START_DATE', payload: initStartDate });
    }
    if (initEndDate) {
      dispatch({ type: 'SET_END_DATE', payload: initEndDate });
    }
    dispatch({ type: 'TOGGLE_RANGE', payload: isRange });
  
  }, [initStartDate, initEndDate,isRange, dispatch]);

  useEffect(() => {
    if (isRange) {
      dispatch({ type: 'TOGGLE_RANGE', payload: true });
    } else {
      dispatch({ type: 'SET_END_DATE', payload: null });
      dispatch({ type: 'TOGGLE_RANGE', payload: false });
    }
  }, [isRange]);

  const modalRoot = document.getElementById('modal-root');

  const isResetDisabled = (initStartDate === state.startDate) && (initEndDate === state.endDate);

  const container = ({ children }: { children: ReactNode }) => {
    return (
      <div className="react-datepicker custom-datepicker" ref={clickRef}>
        <CalendarContainer className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>
              {state.isRange ? t('Pick date range') : t('Pick start date')}
            </div>
            <button
              className={styles.closeButton}
              onClick={() => {
                dispatch({ type: 'SET_START_DATE', payload: initStartDate });
                dispatch({ type: 'SET_END_DATE', payload: initEndDate });
                dispatch({ type: 'SET_START_DATE_ERROR', payload: '' });
                dispatch({ type: 'SET_END_DATE_ERROR', payload: '' });
                dispatch({ type: 'SET_RANGE_ERROR', payload: '' });
                setIsOpen(false);
              }}
            >
              <CloseIcon />
            </button>
          </div>

          <DateRangeInput
            onStartDateChange={(date: string | null) => {
              if (!date) {
                if (state.endDate) {
                  dispatch({ type: 'SET_END_DATE', payload: null });
                }
                dispatch({ type: 'SET_START_DATE', payload: null });
                return;
              }
              let parsedDate = parse(date, 'dd/MM/yyyy', new Date());
              if (state.startDateError) {
                dispatch({ type: 'SET_START_DATE_ERROR', payload: '' });
              }
              dispatch({ type: 'SET_START_DATE', payload: parsedDate });
            }}
            onEndDateChange={(date: string | null) => {
              if (!date) {
                dispatch({ type: 'SET_END_DATE', payload: null });
                return;
              }
              let parsedDate = parse(date, 'dd/MM/yyyy', new Date());
              if (state.endDateError) {
                dispatch({ type: 'SET_END_DATE_ERROR', payload: '' });
              }
              dispatch({ type: 'SET_END_DATE', payload: parsedDate });
            }}
          />
          <div className={styles.divider}></div>

          {showPresets && (
            <DatePresets
              isRange={state.isRange}
              showAllTime={allTimePreset}
              onChange={(start, end) => {
                dispatch({ type: 'SET_START_DATE', payload: start });
                dispatch({ type: 'SET_END_DATE', payload: end });
                dispatch({ type: 'SET_START_DATE_ERROR', payload: '' });
                dispatch({ type: 'SET_END_DATE_ERROR', payload: '' });
                dispatch({ type: 'SET_RANGE_ERROR', payload: '' });
              }}
            />
          )}

          <div
            className={styles.datepickerContainer}
            style={{ position: 'relative' }}
          >
            {children}
          </div>

          <div className={styles.divider}></div>

          <div className={styles.controls}>
            <button
              className='btn-primary'
              onClick={() => {
                // with the "All time" preset the empty selection IS a valid
                // choice — Apply confirms it instead of demanding a start date
                if (allTimePreset && !state.startDate && !state.endDate) {
                  onChange(null, null);
                  setIsOpen(false);
                  return;
                }
                if (!state.startDate || (state.isRange && !state.endDate)) {
                  if (!state.startDate) {
                    dispatch({
                      type: 'SET_START_DATE_ERROR',
                      payload: t('Pick Start date'),
                    });
                    return;
                  }
                  if (state.isRange && !state.endDate) {
                    dispatch({
                      type: 'SET_END_DATE_ERROR',
                      payload: t('Pick End Date'),
                    });
                  }
                  return;
                }
                if (
                  state.rangeError ||
                  state.startDateError ||
                  state.endDateError
                ) {
                  return;
                }
                onChange(state.startDate, state.endDate);
                setIsOpen(false);
              }}
            >
              {t('Apply')}
            </button>
            <button
              className='btn-reset'
              disabled={clearable ? !state.startDate && !state.endDate : isResetDisabled}
              onClick={() => {
                dispatch({ type: 'SET_START_DATE', payload: clearable ? null : initStartDate });
                dispatch({ type: 'SET_END_DATE', payload: clearable ? null : initEndDate });
                dispatch({ type: 'SET_START_DATE_ERROR', payload: '' });
                dispatch({ type: 'SET_END_DATE_ERROR', payload: '' });
                dispatch({ type: 'SET_RANGE_ERROR', payload: '' });
                if (clearable) {
                  onChange(null, null);
                  setIsOpen(false);
                }
              }}
            >
              {t(clearable ? 'Clear' : 'Reset')}
            </button>
          </div>
        </CalendarContainer>
      </div>
    );
  };

  const handleChange = (dates: [Date | null, Date | null]) => {
    setCalendarResetKey(k => k + 1);
    dispatch({ type: 'SET_START_DATE', payload: dates[0] });
    dispatch({ type: 'SET_END_DATE', payload: dates[1] });
    if (dates[0] && state.startDateError) {
      dispatch({ type: 'SET_START_DATE_ERROR', payload: '' });
    }
    if (dates[1] && state.endDateError) {
      dispatch({ type: 'SET_END_DATE_ERROR', payload: '' });
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  let buttonText = emptyLabel ?? t(buttonDateFormat.toUpperCase()) + ' - ' + t(buttonDateFormat.toUpperCase());
  if (state.isRange && (state.startDate || state.endDate)) {
    buttonText = `${
      state.startDate ? format(state.startDate, 'dd/MM/yyyy') : ''
    } - ${state.endDate ? format(state.endDate, buttonDateFormat) : ''}`;
  } else if (!state.isRange && state.startDate) {
    buttonText = format(state.startDate, buttonDateFormat);
  } else if (!state.isRange && !state.startDate) {
    buttonText = t(buttonDateFormat.toUpperCase());
  }

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (
          state.startDate &&
          state.endDate &&
          !state.rangeError &&
          !state.startDateError &&
          !state.endDateError
        ) {
          onChange(state.startDate, state.endDate);
          setIsOpen(false);
        }
      }
      if (e.key === 'Escape') {
        // consume the key so an enclosing Modal doesn't close along with the
        // calendar (capture phase runs before the Modal's document listener,
        // and stopPropagation keeps the event from reaching it)
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: 'SET_START_DATE', payload: initStartDate });
        dispatch({ type: 'SET_END_DATE', payload: initEndDate });
        dispatch({ type: 'SET_START_DATE_ERROR', payload: '' });
        dispatch({ type: 'SET_END_DATE_ERROR', payload: '' });
        dispatch({ type: 'SET_RANGE_ERROR', payload: '' });
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, state, onChange]);

  useEffect(() => {
    if (!isOpen) {
      setCalendarDate(initStartDate || new Date());
      setCalendarResetKey(0);
    }
  }, [isOpen, initStartDate]);

  useEffect(() => {
    if (!isOpen) return;

    // save + restore instead of clearing: inside a Modal the body is already
    // scroll-locked, and blanking overflow here would unlock the whole page
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const commonDatePickerProps = {
    dateFormat: "dd/MM/yyyy",
    selected: state.startDate,
    calendarContainer: container,
    minDate: new Date(2020, 0, 1),
    maxDate: maxDate ?? addMonths(new Date(), 1),
    showDisabledMonthNavigation: true,
    monthsShown,
    inline: true,
    locale: datepickerLocale,
    useWeekdaysShort: true,
    openToDate: calendarDate,
    onMonthChange: (date: Date) => setCalendarDate(date),
  };

  return (
    <div className={className}>
      {state.startDate ? (
        <button
          className={`${styles.button} ${styles.date} ${buttonDateFormat === 'dd/MM/yyyy' ? '' : buttonDateFormat === 'dd/MM/yy' ? styles.dateShort : ''} ${formError ? styles.buttonError : ''}`}
          onClick={handleClick}
          disabled={disabled}
        >
          <span>
            {state.isRange ? format(state.startDate, buttonDateFormat) + ' - ' +
            (state.endDate ? format(state.endDate, buttonDateFormat) : '') : format(state.startDate, buttonDateFormat)}
          </span>
        </button>
      ) : (
        <button
          className={`${styles.button} ${styles.date} ${emptyLabel && emptyLabelActive ? styles.buttonEmptyActive : styles.buttonInactive} ${buttonDateFormat === 'dd/MM/yyyy' ? '' : buttonDateFormat === 'dd/MM/yy' ? styles.dateShort : ''} ${formError ? styles.buttonError : ''}`}
          onClick={handleClick}
          disabled={disabled}
        >
          {buttonText}
        </button>
      )}
      {formError && <div className={styles.formError}>{formError}</div>}
      {isOpen &&
        ReactDOM.createPortal(
          <div className={styles.datepickerWrapper}>
            {state.isRange ? <DatePicker
              key={calendarResetKey}
              {...commonDatePickerProps}
              selectsRange
              onChange={handleChange}
              startDate={state.startDate}
              endDate={state.endDate}
            /> : <DatePicker
              key={calendarResetKey}
              {...commonDatePickerProps}
              onChange={(date: Date | null) => {
                dispatch({ type: 'SET_START_DATE', payload: date });
              }}
            />}
          </div>,
          modalRoot as HTMLElement,
        )}
    </div>
  );
};

export default Datepicker;
